import type { Placement } from "@floating-ui/react"
import {
  autoUpdate,
  arrow,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react"
import type { ReactNode } from "react"
import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"

import { TooltipContext } from "./TooltipContext"
import { TooltipPortal } from "./TooltipPortal"
import { useMouseX } from "./useMouseX"
import { useXPositionAsTime } from "./useXPositionAsTime"
import { useFollowedUsers } from "src/contexts"
import {
  useNearestGlucoseReading,
  useOnMountAndResize,
} from "src/hooks"

interface Props {
  children: ReactNode;
  placement?: Placement;
}

/*
 * this component provides the tooltip context for its children and renders the tooltip portal
 */
export function TooltipContextProvider(props: Props) {
  const {
    children,
    placement = "left",
  } = props

  const { currentFollow } = useFollowedUsers()

  const mouseX = useMouseX()
  const time = useXPositionAsTime(mouseX)
  const reading = useNearestGlucoseReading(
    currentFollow.following_account_id,
    time,
  )

  const containerRef = useRef<HTMLDivElement>(null)

  const [
    containerBoundingClientRect,
    setContainerBoundingClientRect,
  ] = useState<DOMRect>(new DOMRect(
    0,
    0,
    0,
    0,
  ))

  const updateContainerState = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        setContainerBoundingClientRect(element.getBoundingClientRect())
      }
    },
    [setContainerBoundingClientRect],
  )

  const containerRefMountAndResize = useOnMountAndResize(updateContainerState)

  const [
    isOpen,
    setIsOpen,
  ] = useState<boolean>(false)

  const arrowRef = useRef<HTMLDivElement>(null)

  const data = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(16),
      flip({
        boundary: containerRef.current ?? undefined,
      }),
      shift({
        boundary: containerRef.current ?? undefined,
      }),
      arrow({
        element: arrowRef,
      }),
    ],
  })

  const context = data.context
  const hover = useHover(context)
  const interactions = useInteractions([hover])

  const tooltipRef = useMergeRefs<HTMLDivElement>([
    containerRef,
    containerRefMountAndResize,
    data.refs.setReference,
  ])

  const value = useMemo(
    () => ({
      open: isOpen,
      onOpenChange: setIsOpen,
      reading,
      arrow: arrowRef,
      ...data,
      ...interactions,
      tooltipRef,
    }),
    [
      isOpen,
      setIsOpen,
      data,
      reading,
      arrowRef,
      interactions,
      tooltipRef,
    ],
  )

  return (
    <TooltipContext.Provider value={value}>
      {children}
      <TooltipPortal
        containerRef={containerRef}
        containerBoundingClientRect={containerBoundingClientRect}
      />
    </TooltipContext.Provider>
  )
}
